import { Fragment, ImgHTMLAttributes, useEffect, useState } from 'react'

import { getImageUrl } from './helpers'
import { ResizeFit, MediaQueryType } from './types'

interface ColorCode {
  hex: string
  alpha?: number
}

interface Props extends ImgHTMLAttributes<HTMLImageElement> {
  bucket?: string
  className?: string
  imageClassName?: string
  fit?: ResizeFit
  height?: number
  width?: number
  alt?: string
  fetchPriority?: 'low' | 'high' | undefined
  loading?: 'lazy' | 'eager' | undefined
  lazyLoad?: boolean
  fitBgColor?: ColorCode
  quality?: number
  mediaQuery?: MediaQueryType[]
  imageWidths?: string
  types?: string[]
  onClick?: () => void
  onLoadingChange?: (isLoading: boolean) => void
}

interface WithSrcKey {
  srcKey: string
  src?: never
}

interface WithSrc {
  src: string
  srcKey?: never
}

type ImageProps = (WithSrcKey | WithSrc) & Props

const queries: MediaQueryType[] = [
  { screen: '(max-width: 767px)', width: '100vw' },
  { screen: '(min-width: 768px)', width: '50vw' },
]

export default function ResponsiveImage(props: ImageProps): React.ReactElement {
  const {
    alt = '',
    className = '',
    imageClassName = '',
    bucket,
    fit = 'cover',
    height,
    src,
    srcKey,
    width,
    loading = 'lazy',
    fetchPriority,
    fitBgColor,
    mediaQuery = queries,
    imageWidths = '30, 60, 100, 160, 320, 440, 540, 640, 960, 1280, 1536',
    types = ['webp', 'jpg'],
    onClick,
    onLoadingChange,
    ...rest
  } = props

  const [isLoading, setIsLoading] = useState(Boolean(onLoadingChange))

  useEffect(() => {
    if (onLoadingChange) onLoadingChange(isLoading)
  }, [isLoading, onLoadingChange])

  const handleLoadEnd = (): void => {
    if (onLoadingChange) setIsLoading(false)
  }

  if (srcKey) {
    const aspectRatio = height / width
    const { urlOriginal } = getImageUrl(height, width, fit, srcKey, bucket, fitBgColor)
    const imageFactor = fit === 'inside' ? 4 : 1

    const imageWidthsArray = imageWidths.split(',').map((w) => parseInt(w))

    const srcSetForWebp = (webSrc: string): string[] => {
      return imageWidthsArray.map((w) => {
        /**
         * If aspectRatio is not a number (NaN), then we use the width and height props
         * If aspectRatio is a number, then we use the width and calculate the height
         * if height or width is not provided then we use the original image size
         */
        const srcWidth = !aspectRatio ? width ?? undefined : Math.floor(w / imageFactor)
        const srcHeight = !aspectRatio ? height ?? undefined : Math.floor(srcWidth * aspectRatio)

        return `${getImageUrl(srcHeight, srcWidth, fit, webSrc, bucket, fitBgColor).urlWithFormat} ${w}w`
      })
    }
    const srcSetForRaw = (rawSrc: string): string[] => {
      return imageWidthsArray.map((w) => {
        const srcWidth = !aspectRatio ? width ?? undefined : Math.floor(w / imageFactor)
        const srcHeight = !aspectRatio ? width ?? undefined : Math.floor(w * aspectRatio)
        return `${getImageUrl(srcHeight, srcWidth, fit, rawSrc, bucket, fitBgColor).urlOriginal} ${w}w`
      })
    }

    const createSources = (query: MediaQueryType): React.ReactElement => {
      return (
        <Fragment key={query.screen}>
          {types.map((type) => (
            <source
              key={query.screen + type}
              media={query.screen}
              sizes={query.width}
              srcSet={
                type === 'webp'
                  ? srcSetForWebp(query.srcKey ?? srcKey).join(', ')
                  : srcSetForRaw(query.srcKey ?? srcKey).join(', ')
              }
              type={`image/${type}`}
            />
          ))}
        </Fragment>
      )
    }

    return (
      <picture className={className}>
        {mediaQuery.map(createSources)}
        <img
          alt={alt}
          className={imageClassName}
          // eslint-disable-next-line react/no-unknown-property
          fetchPriority={fetchPriority ?? loading === 'lazy' ? 'low' : 'high'}
          height={height}
          loading={loading}
          onClick={onClick}
          onLoad={handleLoadEnd}
          sizes={mediaQuery[0].width}
          src={urlOriginal}
          srcSet={srcSetForWebp(mediaQuery[0].srcKey ?? srcKey).join(', ')}
          width={width}
          {...rest}
        />
      </picture>
    )
  }
  return (
    <picture className={className}>
      <img
        alt={alt}
        className={imageClassName}
        // eslint-disable-next-line
        fetchPriority={fetchPriority ?? loading === 'lazy' ? 'low' : 'high'}
        height={height}
        loading={loading}
        onClick={onClick}
        onLoad={handleLoadEnd}
        sizes={mediaQuery[0].width}
        src={src}
        width={width}
      />
    </picture>
  )
}
